import React from 'react';
import { usePermission } from '../../hooks';

/**
 * Permission guard component that conditionally renders its children
 * based on the user's permission.
 *
 * Uses the `usePermission` hook to determine if the user has the required permission.
 * If permission is granted, renders children. Otherwise, renders the fallback (if provided) or nothing.
 *
 * @param {Object} props
 * @param {string} props.permission - The permission key to check (from {@link PERMISSIONS}).
 * @param {React.ReactNode} props.children - The content to render if permission is granted.
 * @param {React.ReactNode} [props.fallback=null] - The content to render if permission is denied.
 * @returns {React.ReactNode} Rendered children or fallback.
 *
 * @example
 * <Permission permission={PERMISSIONS.MANAGE_USERS} fallback={<span>No Access</span>}>
 *   <button>Edit User</button>
 * </Permission>
 */
export const Permission = ({ permission, children, fallback = null }) => {
    const hasAccess = usePermission(permission);

    if (!hasAccess) {
        return fallback;
    }

    return <>{children}</>;
};
